package com.flowyride.feature.account

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.flowyride.core.domain.account.AccountException
import com.flowyride.core.domain.account.AccountRepository
import com.flowyride.core.domain.account.AccountRules
import com.flowyride.core.domain.account.RegistrationRequest
import com.flowyride.feature.account.resources.Res
import com.flowyride.feature.account.resources.error_email_invalid
import com.flowyride.feature.account.resources.error_fields_required
import com.flowyride.feature.account.resources.error_message_long
import com.flowyride.feature.account.resources.error_password_short
import com.flowyride.feature.account.resources.error_username_rules
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.StringResource

enum class AuthMode { SignIn, Request }

data class AuthUiState(
    val mode: AuthMode = AuthMode.SignIn,
    val login: String = "",
    val password: String = "",
    val username: String = "",
    val email: String = "",
    val newPassword: String = "",
    val message: String = "",
    val busy: Boolean = false,
    val error: StringResource? = null,
    /** The access request went through: shown on the sign-in tab. */
    val requestSent: Boolean = false,
)

sealed interface AuthAction {
    data class ModeSelected(val mode: AuthMode) : AuthAction
    data class LoginChanged(val value: String) : AuthAction
    data class PasswordChanged(val value: String) : AuthAction
    data class UsernameChanged(val value: String) : AuthAction
    data class EmailChanged(val value: String) : AuthAction
    data class NewPasswordChanged(val value: String) : AuthAction
    data class MessageChanged(val value: String) : AuthAction
    data object SignIn : AuthAction
    data object SendRequest : AuthAction
}

/** Sign in, or ask the admin for an account. Once signed in, [AccountRepository.session] switches the app over. */
class AuthViewModel(private val accounts: AccountRepository) : ViewModel() {

    private val _state = MutableStateFlow(AuthUiState())
    val state: StateFlow<AuthUiState> = _state.asStateFlow()

    fun onAction(action: AuthAction) {
        when (action) {
            is AuthAction.ModeSelected -> _state.update { it.copy(mode = action.mode, error = null) }
            is AuthAction.LoginChanged -> _state.update { it.copy(login = action.value, error = null) }
            is AuthAction.PasswordChanged -> _state.update { it.copy(password = action.value, error = null) }
            is AuthAction.UsernameChanged -> _state.update { it.copy(username = action.value, error = null) }
            is AuthAction.EmailChanged -> _state.update { it.copy(email = action.value, error = null) }
            is AuthAction.NewPasswordChanged -> _state.update { it.copy(newPassword = action.value, error = null) }
            is AuthAction.MessageChanged -> _state.update { it.copy(message = action.value, error = null) }
            AuthAction.SignIn -> signIn()
            AuthAction.SendRequest -> sendRequest()
        }
    }

    private fun signIn() {
        val current = _state.value
        if (current.busy) return
        if (current.login.isBlank() || current.password.isEmpty()) {
            _state.update { it.copy(error = Res.string.error_fields_required) }
            return
        }
        run { accounts.signIn(current.login, current.password) }
    }

    private fun sendRequest() {
        val current = _state.value
        if (current.busy) return
        val request = RegistrationRequest(
            username = current.username.trim(),
            email = current.email.trim(),
            password = current.newPassword,
            message = current.message.trim().ifBlank { null },
        )
        validationError(request)?.let { error ->
            _state.update { it.copy(error = error) }
            return
        }
        run(
            onSuccess = {
                it.copy(
                    mode = AuthMode.SignIn,
                    requestSent = true,
                    login = request.username,
                    password = "",
                    newPassword = "",
                    message = "",
                )
            },
        ) { accounts.register(request) }
    }

    private fun run(onSuccess: (AuthUiState) -> AuthUiState = { it }, block: suspend () -> Unit) {
        _state.update { it.copy(busy = true, error = null) }
        viewModelScope.launch {
            try {
                block()
                _state.update { onSuccess(it).copy(busy = false) }
            } catch (e: AccountException) {
                _state.update { it.copy(busy = false, error = e.toMessage()) }
            }
        }
    }

    private fun validationError(request: RegistrationRequest): StringResource? = when {
        !AccountRules.isValidUsername(request.username) -> Res.string.error_username_rules
        !AccountRules.isValidEmail(request.email) -> Res.string.error_email_invalid
        !AccountRules.isValidPassword(request.password) -> Res.string.error_password_short
        (request.message?.length ?: 0) > AccountRules.MESSAGE_MAX -> Res.string.error_message_long
        else -> null
    }
}
